/**
 * Inline per-unit availability calendar on the Create Contract page.
 *
 * Only one calendar can be open at a time. This is intentional: drag.ts and
 * pricing.ts hold module-level state, so rendering two grids simultaneously
 * would cause the drag handler to point at whichever unit was initialised last.
 * The single-open invariant keeps this coherent without per-instance refactoring.
 */

import type { CalendarData, TweakPrefs } from '../availability-calendar/types';
import { setPricingData, toYmd, dateRange, addDays } from '../availability-calendar/pricing';
import { renderGrid } from '../availability-calendar/grid';
import { initDrag, cancelDrag } from '../availability-calendar/drag';
import { getCellState } from '../availability-calendar/bands';

export interface UnitCalendarContext {
    dataUrl: string;
    i18n: Record<string, string>;
    currency: string;
}

const _dataCache = new Map<string, CalendarData>();

export function clearUnitCalendarCache(): void {
    _dataCache.clear();
}

// Track containers that already have a delegated click listener so re-renders don't stack listeners.
const _wiredRoots = new WeakSet<HTMLElement>();

export function setupUnitCalendarToggles(root: HTMLElement, ctx: UnitCalendarContext): void {
    // Set titles on currently-visible buttons (safe to do on every render).
    root.querySelectorAll<HTMLButtonElement>('.cc-toggle-calendar').forEach(btn => {
        btn.title = ctx.i18n['showCalendar'] || 'Show calendar';
    });

    // Register the delegated click listener only once per container.
    if (_wiredRoots.has(root)) return;
    _wiredRoots.add(root);

    root.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.cc-toggle-calendar');
        if (btn) toggleCalendar(btn, root, ctx);
    });
}

function toggleCalendar(btn: HTMLButtonElement, root: HTMLElement, ctx: UnitCalendarContext): void {
    const unitId = parseInt(btn.dataset.unitId || '0', 10);
    const mount = root.querySelector<HTMLElement>(`.cc-unit-calendar[data-unit-id="${unitId}"]`);
    if (!mount) return;

    const isOpen = !mount.classList.contains('d-none');

    // Cancel any in-progress drag before tearing down the open calendar.
    cancelDrag();

    // Collapse every open calendar (single-open invariant)
    root.querySelectorAll<HTMLElement>('.cc-unit-calendar').forEach(m => {
        if (!m.classList.contains('d-none')) {
            m.classList.add('d-none');
            m.innerHTML = '';
        }
    });
    root.querySelectorAll<HTMLButtonElement>('.cc-toggle-calendar').forEach(b => {
        b.setAttribute('aria-expanded', 'false');
        b.title = ctx.i18n['showCalendar'] || 'Show calendar';
    });

    if (isOpen) return;

    btn.setAttribute('aria-expanded', 'true');
    btn.title = ctx.i18n['hideCalendar'] || 'Hide calendar';
    mount.classList.remove('d-none');

    loadAndRender(mount, unitId, ctx);
}

async function loadAndRender(
    mount: HTMLElement,
    unitId: number,
    ctx: UnitCalendarContext,
): Promise<void> {
    const card = mount.closest<HTMLElement>('.cc-unit-selection-card');
    const fromInput = card?.querySelector<HTMLInputElement>('.sub-range-from-picker');
    const toInput   = card?.querySelector<HTMLInputElement>('.sub-range-to-picker');
    const offerId   = mount.dataset.offerId || '';

    // Date window from the input constraints
    const today = toYmd(new Date());
    const rawMin = fromInput?.min || '';
    const rawMax = toInput?.max   || '';
    const windowFrom = rawMin && rawMin > today ? rawMin : (rawMin || today);
    let windowTo     = rawMax || toYmd(addDays(new Date(), 90));

    // Guarantee at least a 60-day window so the calendar is useful
    const span = Math.round(
        (new Date(windowTo + 'T00:00:00').getTime() - new Date(windowFrom + 'T00:00:00').getTime()) / 86400000,
    );
    if (span < 60) windowTo = toYmd(addDays(new Date(windowFrom + 'T00:00:00'), 60));

    mount.innerHTML = `<p class="text-muted small p-2">${ctx.i18n['loading'] || 'Loading…'}</p>`;

    let data = _dataCache.get(offerId);
    if (!data) {
        try {
            const url = `${ctx.dataUrl}?offerId=${encodeURIComponent(offerId)}&from=${encodeURIComponent(windowFrom)}&to=${encodeURIComponent(windowTo)}`;
            const resp = await fetch(url, { credentials: 'same-origin' });
            const json = await resp.json() as { success: boolean; data: CalendarData };
            if (!json.success) throw new Error('bad response');
            data = json.data;
            _dataCache.set(offerId, data);
        } catch {
            mount.innerHTML = `<p class="text-muted small p-2">${ctx.i18n['calendarError'] || 'Could not load calendar.'}</p>`;
            return;
        }
    }

    const thisUnit  = data.units.find(u => u.id === unitId);
    const thisAccom = thisUnit ? data.accommodations.find(a => a.id === thisUnit.accomId) : null;
    if (!thisUnit || !thisAccom) {
        mount.innerHTML = '';
        return;
    }

    setPricingData(data.pricePeriods, data.bookings);

    const days     = dateRange(windowFrom, windowTo);
    const todayYmd = today;
    let drag       = null as Parameters<typeof renderGrid>[0]['drag'];

    const prefs: TweakPrefs = {
        density: 'compact',
        groupByAccom: false,
        showWeekends: true,
        priceDisplay: 'number',
        showGuestNames: false,
    };

    mount.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'availability-calendar contract-create-calendar';
    const grid = document.createElement('div');
    wrapper.appendChild(grid);
    mount.appendChild(wrapper);

    const capturedData  = data;
    const capturedDays  = days;

    function redraw(newDrag: typeof drag): void {
        drag = newDrag;
        renderGrid({
            container: grid,
            accoms: [thisAccom!],
            units: [thisUnit!],
            bookings: capturedData.bookings,
            days: capturedDays,
            todayYmd,
            prefs,
            i18n: ctx.i18n,
            drag,
            visibleStates: new Set(['booked', 'option', 'blocked']),
            activeOnly: false,
            hasPriceOnly: false,
            search: '',
            typeFilter: new Set(),
            serviceFilter: new Set(),
            classifFilter: new Set(),
            priceMin: 0,
            priceMax: Infinity,
            cellW: 28,
            labelW: 120,
            currency: ctx.currency,
        });
    }

    redraw(null);

    initDrag({
        getState: (uid, dayIdx) =>
            getCellState(uid, toYmd(capturedDays[dayIdx]), todayYmd, capturedData.bookings),
        getDays: () => capturedDays,
        getRowData: (uid) => {
            const u = capturedData.units.find(x => x.id === uid);
            return u ? { accomId: u.accomId } : null;
        },
        redraw,
        onSelect: (draft) => {
            if (fromInput) {
                fromInput.value = draft.from;
                fromInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
            if (toInput) {
                toInput.value = draft.to;
                toInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        },
    });
}
